import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { injectIntl } from 'react-intl';
import SidePanel from 'common/side-panel/side-panel';
import Spinner from 'common/spinner/components/spinner';
import useDeepCompareEffect from 'use-deep-compare-effect';
import { useNavigate, useParams } from 'react-router-dom';
import { Col, Form } from 'antd';
import { FormField, Dropdown, Input, Button, Row } from 'ui';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import {
  addMasterData,
  fetchWizardData,
  updateMasterData,
  fetchSkill,
  resetSkill,
} from '../../containers/skills/skills-actions';

const StyledButton = styled(Button)`
  text-transform: uppercase;
  cursor: ${p => p.disabled ? 'not-allowed' : 'pointer'};
`;

const Buttons = styled(Row)`
  gap: 20px;
`;

const SaveSkillSidepanel = ({ intl }) => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const params = useParams();
  const dispatch = useDispatch();
  const {
    updateMasterDataSuccess,
    isUpdatingMasterData,
    isLoadingWizardData,
    wizardData,
    skill = {},
    isLoadingSkill,
  } = useSelector(state => state.skills);

  const close = () => {
    navigate(-1);
  };

  const handleSave = () => {
    if (params?.id) {
      dispatch(updateMasterData({ id: params?.id, ...form.getFieldsValue() }));
    } else {
      dispatch(addMasterData({ ...form.getFieldsValue() }));
    }
  };

  useEffect(() => {
    dispatch(fetchWizardData());
    return () => dispatch(resetSkill());
  }, []);

  useEffect(() => {
    if (params.id) {
      dispatch(fetchSkill(params.id));
    }
  }, [params.id]);

  useDeepCompareEffect(() => {
    if (skill) {
      form.setFieldsValue({
        skillType: skill.skillType?.id,
        name: skill.name,
        scope: skill.scope,
        description: skill.description,
      });
    }
  }, [skill]);

  useDeepCompareEffect(() => {
    if (wizardData.skillTypes?.length === 1) {
      form.setFieldsValue({
        skillType: wizardData.skillTypes[0].id,
      });
    }
    if (wizardData.serviceCenters?.length === 1) {
      form.setFieldsValue({
        scope: wizardData.serviceCenters[0].id,
      });
    }
  }, [wizardData]);

  useEffect(() => {
    if (updateMasterDataSuccess) {
      close();
    }
  }, [updateMasterDataSuccess]);

  return (
    <>
      <SidePanel
        width={'40%'}
        title={intl.formatMessage({ id: `skills.${params.id ? 'EDIT_SKILL' : 'NEW_SKILL'}` })}
        onPanelClose={close}
        contentOverflow={false}
      >
        {isLoadingWizardData || isLoadingSkill ?
          <Spinner /> :
          <Form
            layout='vertical'
            onFinish={handleSave}
            requiredMark={false}
            form={form}
          >
            <Row>
              <Col span={8}>
                <FormField
                  label={intl.formatMessage({
                    id: 'skills.TYPE',
                  })}
                  name='skillType'
                  rules={[{
                    required: true,
                    message: intl.formatMessage({ id: 'rules.SKILL_TYPE' }),
                  }]}
                >
                  <Dropdown
                    values={wizardData?.skillTypes || []}
                    valueKey='id'
                    valueLabel='description'
                    disabled={isUpdatingMasterData || !!params.id}
                  />
                </FormField>
              </Col>
            </Row>
            <Row>
              <Col span={8}>
                <FormField
                  label={intl.formatMessage({
                    id: 'skills.SCOPE',
                  })}
                  name='scope'
                  rules={[{ required: true, message: intl.formatMessage({ id: 'rules.SCOPE' }) }]}
                >
                  <Dropdown
                    values={wizardData?.serviceCenters || []}
                    valueKey='id'
                    valueLabel='name'
                    disabled={isUpdatingMasterData || !!params.id}
                  />
                </FormField>
              </Col>
            </Row>
            <Row>
              <Col span={4}>
                <FormField
                  label={intl.formatMessage({
                    id: 'skills.SKILL',
                  })}
                  name='name'
                  rules={[{ required: true, message: intl.formatMessage({ id: 'rules.SKILL' }) }]}
                >
                  <Input disabled={isUpdatingMasterData} />
                </FormField>
              </Col>
            </Row>
            <Row>
              <Col span={10}>
                <FormField
                  label={intl.formatMessage({
                    id: 'skills.DESCRIPTION',
                  })}
                  name='description'
                  rules={[{
                    required: true,
                    message: intl.formatMessage({ id: 'rules.SKILL_DESCRIPTION' }),
                  }]}
                >
                  <Input disabled={isUpdatingMasterData} />
                </FormField>
              </Col>
            </Row>
            <Buttons marginTop={20}>
              <StyledButton onClick={close} disabled={isUpdatingMasterData}>
                {intl.formatMessage({ id: 'common.CANCEL' })}
              </StyledButton>
              <StyledButton
                htmlType='submit'
                type='secondary'
                disabled={isUpdatingMasterData}
              >
                {intl.formatMessage({ id: 'skills.SAVE_SKILL' })}
              </StyledButton>
            </Buttons>
          </Form>
        }
      </SidePanel>
    </>
  );
};

SaveSkillSidepanel.propTypes = {
  intl: PropTypes.object,
};

export default injectIntl(SaveSkillSidepanel);
